import { Alert, Badge, Button, Group, List, Stack, Text } from '@mantine/core'
import { IconGitPullRequest } from '@tabler/icons-react'
import type { PedidoNoCodigo } from '@/api/waf'
import type { PedidoDoCartao } from '@/api/terraformRunner'
import { LinhaDoTempoDoPedido } from '@/pages/chat/components/LinhaDoTempoDoPedido'
import { PainelDeExecucao, totalDoPlan } from '@/pages/chat/components/PainelDeExecucao'
import { DecisaoDoPedido } from '@/components/chamados/DecisaoDoPedido'
import { descreverAcao } from './acoesDoModulo'

const ESTADO: Record<string, string> = {
  rascunho: 'rascunho',
  planejando: 'rodando o plan',
  plano_pronto: 'plan pronto',
  aguardando_confirmacao: 'espera o seu Confirmar',
  confirmado: 'confirmado',
  aguardando_aprovacao: 'espera um DevOps Sênior aprovar',
  mr_aberto: 'MR aberto no repositório do cliente',
  aguardando_gmud: 'espera a GMUD',
  mergeado: 'mergeado',
  replanejando: 'replanejando o código mergeado',
  aplicando: 'aplicando',
  falha_de_execucao: 'falhou na execução',
  na_fila: 'Na fila',
  aguardando_pedinte: 'Esperando o pedinte',
}

/**
 * O pedido que ocupa o stage (spec 2026-10-14 D6). Desde a spec 2026-10-15
 * (WAF 100% D1), o pedido da tela vai do plan direto para a aprovação do
 * DevOps Sênior: não há Confirmar. O botão abaixo só aparece para pedido que
 * já estava em `aguardando_confirmacao` antes da mudança — só de quem pediu, e
 * a rota recusa qualquer outro caso.
 *
 * `acompanhamento` é o mesmo corpo que o cartão do chat lê
 * (`GET /terraform/change-requests/{id}`, que repergunta sozinho): quando
 * chega, o estado sai dele, e não da carga do modo código, que só relê ao
 * voltar o foco. Com ele vêm a linha do tempo e o log da execução — o mesmo
 * acompanhamento do provisionamento pelo chat, sem rota nova.
 */
export function PainelPedidoNoCodigo({
  pedido, souRequerente, onConfirmar, confirmando, erro, acompanhamento = null, onDecidido = () => {},
  parado = false, onRetomar, retomando = false,
}: {
  pedido: PedidoNoCodigo & { posicao?: number | null }
  souRequerente: boolean
  onConfirmar: () => void
  confirmando: boolean
  erro: string | null
  acompanhamento?: PedidoDoCartao | null
  onDecidido?: () => void
  /** Veio de `pedido_parado`: já terminou, antes mesmo de o acompanhamento chegar. */
  parado?: boolean
  /** O "Quero seguir" do livre guardado (spec 2026-10-15 D3); aparece só com
   * `pode_retomar` do acompanhamento, que é de quem pediu. */
  onRetomar?: () => void
  retomando?: boolean
}) {
  const estado = acompanhamento?.estado ?? pedido.estado
  const podeConfirmar = souRequerente && estado === 'aguardando_confirmacao'
  const posicao = acompanhamento?.posicao ?? pedido.posicao ?? null
  const podeRetomar = Boolean(acompanhamento?.pode_retomar && onRetomar)
  // Terminal: o pedido parado sem chamado (`pedido_parado`) — fica à vista até
  // um pedido novo, e não ocupa o stage.
  const terminal = acompanhamento?.terminal ?? parado
  const recusado = pedido.aprovado_no_plan === false || (terminal && estado !== 'concluido')
  const rotulo = ESTADO[estado]
    ?? acompanhamento?.linha_do_tempo.etapas.find(e => e.estado === estado)?.rotulo
    ?? estado
  return (
    <Alert
      color={recusado ? 'red' : 'blue'}
      variant="light"
      icon={<IconGitPullRequest size={20} />}
      title={terminal
        ? <>Último pedido <Text span ff="monospace" fw={600}>#{pedido.id}</Text> do stage</>
        : <>Pedido <Text span ff="monospace" fw={600}>#{pedido.id}</Text> no stage</>}
    >
      <Stack gap={6}>
        <Group gap="xs">
          <Badge variant="light" color={recusado ? 'red' : 'blue'}>
            {rotulo}
          </Badge>
          {pedido.origem !== 'tela_waf' && (
            <Text size="xs" c="dimmed">pedido aberto pelo {pedido.origem === 'chat' ? 'chat' : pedido.origem}</Text>
          )}
          {pedido.mr_iid != null && (
            <Text size="xs" ff="monospace">MR !{pedido.mr_iid}</Text>
          )}
        </Group>
        {pedido.waf_acao && <Text size="sm">{descreverAcao(pedido.waf_acao)}</Text>}
        {/* Spec 2026-10-15 §5.8/D3: a fila diz o lugar; o guardado, o que falta. */}
        {estado === 'na_fila' && posicao != null && (
          <Text size="sm">
            <Text span ff="monospace" size="sm">{`${posicao}º`}</Text> na fila do stage; roda quando chegar a vez, sem precisar pedir de novo.
          </Text>
        )}
        {estado === 'aguardando_pedinte' && (
          <Text size="sm">
            {acompanhamento?.base_mudou
              ? 'O código do stage mudou nos arquivos deste ajuste desde o pedido. Peça o ajuste de novo: o pedido novo fica com o lugar deste.'
              : 'A vez passou sem confirmação. O pedido fica guardado por 24 h, sem ocupar o stage.'}
          </Text>
        )}
        {pedido.motivo && <Text size="sm" c={recusado ? 'red' : 'dimmed'}>{pedido.motivo}</Text>}
        {pedido.waf_reconciliado.length > 0 && !terminal && (
          <>
            <Text size="sm">O apply também leva a AWS de volta ao código em:</Text>
            <List size="sm" spacing={2}>
              {pedido.waf_reconciliado.map(e => (
                <List.Item key={e}><Text span ff="monospace" size="sm">{e}</Text></List.Item>
              ))}
            </List>
          </>
        )}
        {acompanhamento && (
          <Stack gap={6} mt={4}>
            {acompanhamento.linha_plan && (
              <Text size="sm" ff="monospace">{acompanhamento.linha_plan}</Text>
            )}
            <LinhaDoTempoDoPedido linha={acompanhamento.linha_do_tempo} mrUrl={acompanhamento.mr_url} />
            <PainelDeExecucao pedidoId={acompanhamento.id} disponivel={acompanhamento.andamento_disponivel}
                              acoes={acompanhamento.acoes} totalDeMudancas={totalDoPlan(acompanhamento.linha_plan)} />
            {/* Quem aprova está olhando o WAF: decide aqui mesmo, sem chamado. */}
            <DecisaoDoPedido pedido={acompanhamento} onDecidido={onDecidido} />
          </Stack>
        )}
        {erro && <Text size="sm" c="red">{erro}</Text>}
        {podeConfirmar && (
          <Group>
            <Button size="xs" onClick={onConfirmar} loading={confirmando}>Confirmar</Button>
          </Group>
        )}
        {podeRetomar && (
          <Group>
            <Button size="xs" onClick={onRetomar} loading={retomando}>Quero seguir</Button>
          </Group>
        )}
        {!souRequerente && estado === 'aguardando_confirmacao' && (
          <Text size="xs" c="dimmed">Só quem pediu confirma.</Text>
        )}
      </Stack>
    </Alert>
  )
}
